// frontend/src/notifications/useNotifications.js
import { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import { toast } from 'sonner'

const API = process.env.REACT_APP_API_BASE_URL

export function useNotifications() {
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(true)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const [list, count] = await Promise.all([
        axios.get(`${API}/api/notify/notifications`),
        axios.get(`${API}/api/notify/notifications/unread-count`),
      ])
      setNotifications(list.data.notifications || [])
      setUnreadCount(count.data.count || 0)
    } catch (e) {
      // silent — leave previous state
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  // SSE for live updates — needs a signed short-lived token because
  // EventSource can't set Authorization headers. Token is fetched from
  // the auth-protected /sse-token endpoint, then passed as ?token=.
  // The token expires after 15 min; on error we close + refetch + reopen
  // so long-lived sessions don't get stuck in a 401-retry loop.
  useEffect(() => {
    let es = null
    let cancelled = false
    let reconnectTimer = null

    const open = async () => {
      if (cancelled) return
      try {
        const { data } = await axios.get(`${API}/api/notify/sse-token`)
        if (cancelled || !data?.token) return
        es = new EventSource(`${API}/api/notify/events/stream?token=${encodeURIComponent(data.token)}`)
        es.addEventListener('notification', (ev) => {
          try {
            const payload = JSON.parse(ev.data)
            const opts = { description: payload.body, action: payload.deep_link ? { label: 'Open', onClick: () => window.location.assign(payload.deep_link) } : undefined }
            if (payload.urgency === 'sla_breach') toast.error(payload.title, opts)
            else if (payload.urgency === 'high') toast.warning(payload.title, opts)
            else toast(payload.title, opts)
          } catch {}
          fetchAll()
        })
        es.onerror = () => {
          // Likely token expired or server cycled. Close, refetch token, reopen.
          if (es) { try { es.close() } catch {} ; es = null }
          if (!cancelled && !reconnectTimer) {
            reconnectTimer = setTimeout(() => {
              reconnectTimer = null
              open()
            }, 5000)
          }
        }
      } catch {
        // No session yet, or backend down; quietly retry later.
        if (!cancelled && !reconnectTimer) {
          reconnectTimer = setTimeout(() => {
            reconnectTimer = null
            open()
          }, 30000)
        }
      }
    }

    open()
    return () => {
      cancelled = true
      if (reconnectTimer) clearTimeout(reconnectTimer)
      if (es) es.close()
    }
  }, [fetchAll])

  const markSeen = useCallback(async (id) => {
    await axios.post(`${API}/api/notify/notifications/${id}/seen`)
    fetchAll()
  }, [fetchAll])

  const markAllSeen = useCallback(async () => {
    await axios.post(`${API}/api/notify/notifications/mark-all-seen`)
    fetchAll()
  }, [fetchAll])

  const softDelete = useCallback(async (id) => {
    await axios.post(`${API}/api/notify/notifications/${id}/delete`)
    fetchAll()
  }, [fetchAll])

  return { notifications, unreadCount, loading, markSeen, markAllSeen, softDelete, refetch: fetchAll }
}
